import { useState } from 'react';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  type SelectChangeEvent,
  styled,
  Typography,
} from '@mui/material';
import { ExpandDropdownIcon } from '../icons/ExpandArrowIcon';
import FilterIcon from '../icons/FilterIcon';

export type DropdownFilterValueProps<T> = {
  label: string;
  value: T;
};

export type DropdownFilterProps<T> = {
  items: DropdownFilterValueProps<T>[];
  initValue: T;
  onChange: (value: T) => void;
};

const StyledSelect = styled(Select)(() => ({
  fontFamily: '"Segoe UI", sans-serif !important',
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: '0 !important',
    fontFamily: '"Segoe UI", sans-serif !important',
  },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&.MuiInputBase-root': {
    backgroundColor: 'white',
    borderRadius: '1px',
  },
  '& .MuiSelect-icon': {
    padding: '1px',
    right: '8px',
    width: '28px',
    height: '28px',
    boxSizing: 'border-box',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.3, 2),
  fontFamily: '"Segoe UI", sans-serif !important',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
  '&.Mui-selected': { backgroundColor: 'transparent' },
  '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04) !important' },
  '&.Mui-selected:focus': { backgroundColor: 'transparent' },
}));

const SelectContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '3px 8px',
});

export const DropdownFilter = <T extends { key: string | number }>({
  items,
  initValue,
  onChange,
}: DropdownFilterProps<T>) => {
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<T>(initValue);

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const selectedItem = items.find((item) => item.value.key === event.target.value);
    if (!selectedItem) return;
    setSelectedValue(selectedItem.value);
    onChange(selectedItem.value);
  };

  const selectedLabel = items.find((item) => item.value.key === selectedValue.key)?.label || '';

  return (
    <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
      <StyledSelect
        value={selectedValue.key}
        onChange={handleChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        IconComponent={(props) => <ExpandDropdownIcon isOpen={open} {...props} />}
        renderValue={() => (
          <SelectContent>
            <div
              style={{
                paddingRight: '5px',
                height: '25px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FilterIcon />
            </div>
            <Typography
              variant="body2"
              sx={{
                flexGrow: 1,
                fontSize: '16px',
                fontFamily: '"Segoe UI", sans-serif !important',
              }}
            >
              {selectedLabel}
            </Typography>
          </SelectContent>
        )}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.2,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e0e0e0',
              padding: 0,
            },
          },
          MenuListProps: { sx: { paddingTop: 0, paddingBottom: 0 } },
        }}
      >
        {items.map((item) => {
          const isSelected = item.value.key === selectedValue.key;
          return (
            <StyledMenuItem key={item.value.key} value={item.value.key}>
              {isSelected ? (
                <RadioButtonCheckedIcon sx={{ color: '#95C6EF', fontSize: 22 }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ color: '#95C6EF', fontSize: 22 }} />
              )}
              <Typography
                variant="body2"
                sx={{ fontSize: '16px', fontFamily: '"Segoe UI", sans-serif !important' }}
              >
                {item.label}
              </Typography>
            </StyledMenuItem>
          );
        })}
      </StyledSelect>
    </FormControl>
  );
};
